// AnnotateOverlay — entourer ce qui ne va pas, au lieu de le décrire.
//
// Demande de l'opérateur, le 2026-10-04 : « enable user to click on the uploaded photos or
// screen captures and draw on them ».
//
// ── CE QUE CE FICHIER NE DÉCIDE PAS ─────────────────────────────────────────
//
// Aucune règle. La géométrie (du doigt aux pixels de l'image), le filtrage des points,
// l'épaisseur à l'échelle, l'annulation et les couleurs vivent dans src/lib/annotate.ts, en
// fonctions pures, éprouvées par 23 tests et 17 mutations. Ici il n'y a que ce qu'on ne peut
// pas éprouver sans navigateur : écouter un doigt, et peindre.
//
// ── LES TROIS CHOIX QUI ÉVITENT UN DÉFAUT ───────────────────────────────────
//
// 1. LE CANEVAS EST AUX DIMENSIONS DE L'IMAGE, et le CSS l'étire dans sa boîte. Les traits
//    sont donc dessinés dans le MÊME repère que celui où ils sont stockés : une seule
//    conversion dans tout l'écran (le point du doigt), au lieu d'une à l'aller et d'une au
//    retour qui finiraient par diverger.
//
// 2. `touch-action: none`. Sans ça, un glissement du doigt FAIT DÉFILER la page au lieu de
//    dessiner — et sur un téléphone la fonction serait inutilisable sans que rien ne le dise.
//
// 3. `setPointerCapture`. Un geste qui sort du canevas continue d'être livré, donc le trait
//    s'arrête au bord de l'image (où `toImagePoint` le ramène) au lieu de rester ouvert
//    jusqu'au prochain clic quelque part dans la page.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ANNOTATE_COLORS,
  appendPoint,
  newStroke,
  strokeWidthFor,
  toImagePoint,
  undoStroke,
  type Stroke,
} from "../lib/annotate";
import { paintStrokes } from "../lib/paint_strokes";

export default function AnnotateOverlay({
  url, width, height, strokes, onSave, onClose,
}: {
  /** L'image À ANNOTER : l'ORIGINALE, sans les traits déjà posés — ils sont redessinés
   *  par-dessus depuis `strokes`. Sinon ils apparaîtraient deux fois. */
  url:     string;
  width:   number;
  height:  number;
  strokes: readonly Stroke[];
  onSave:  (strokes: readonly Stroke[]) => void;
  onClose: () => void;
}) {
  const [traits, setTraits] = useState<readonly Stroke[]>(strokes);
  const [couleur, setCouleur] = useState<string>(ANNOTATE_COLORS[0]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  /** Le trait en cours. Une référence et pas un état : il change à chaque mouvement du
   *  doigt, et passer par un rendu React à chaque point rendrait le tracé saccadé. */
  const enCours = useRef<Stroke | null>(null);

  // Échap ferme, comme toute vue par-dessus. Rien n'est piégé ici — mais chercher le bouton
  // de fermeture dans un écran qu'on vient de dessiner est une friction gratuite.
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  /** Repeindre : l'image, puis les traits. Le canevas étant aux dimensions de l'image, tout
   *  se dessine dans le repère où les traits sont stockés — sans conversion. */
  const repeindre = useCallback((liste: readonly Stroke[], vif: Stroke | null) => {
    const c = canvasRef.current, img = imgRef.current;
    if (c === null || img === null) return;
    const ctx = c.getContext("2d");
    if (ctx === null) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    // LA définition unique, partagée avec l'enregistrement (lib/paint_strokes). Deux
    // peintures écrites séparément finiraient par diverger, et l'image ENVOYÉE ne montrerait
    // pas ce qu'on voit ici — sans que personne ne s'en aperçoive.
    paintStrokes(ctx, [...liste, ...(vif ? [vif] : [])]);
  }, []);

  useEffect(() => { repeindre(traits, enCours.current); }, [traits, repeindre]);

  const pointDe = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      point: toImagePoint({ x: e.clientX, y: e.clientY }, r, { width, height }),
      epaisseur: strokeWidthFor(r, { width, height }),
    };
  };

  const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const { point, epaisseur } = pointDe(e);
    if (point === null) return;
    // Le geste reste à nous même s'il sort du canevas.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* le navigateur refuse : le geste s'arrêtera au bord */ }
    enCours.current = appendPoint(newStroke(couleur, epaisseur), point);
    repeindre(traits, enCours.current);
  };

  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (enCours.current === null) return;
    const { point } = pointDe(e);
    if (point === null) return;
    const suivant = appendPoint(enCours.current, point);
    // RIEN DE NEUF ⇒ RIEN À REPEINDRE. `appendPoint` rend la MÊME référence quand le point
    // n'apporte pas de déplacement, et c'est ce qui garde le tracé fluide.
    if (suivant === enCours.current) return;
    enCours.current = suivant;
    repeindre(traits, suivant);
  };

  const onUp = () => {
    const t = enCours.current;
    enCours.current = null;
    if (t === null || t.points.length === 0) return;
    setTraits((avant) => [...avant, t]);
  };

  const bouton = "rounded-md border border-border bg-surface px-3 py-1.5 text-[12px] font-medium text-text-secondary transition hover:text-text disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Draw on the screenshot">
      {/* La barre d'outils EN HAUT : sur un téléphone, le bas de l'écran est sous le pouce
          pendant qu'on dessine, et des boutons y seraient touchés par accident. */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        {ANNOTATE_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCouleur(c)}
            aria-label={`Draw in ${c}`}
            aria-pressed={couleur === c}
            // 28 px : au-dessus du minimum de WCAG 2.5.8 (24), et visé au doigt.
            className={`h-7 w-7 rounded-full border-2 transition ${couleur === c ? "border-white" : "border-transparent"}`}
            style={{ backgroundColor: c }}
          />
        ))}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
        <button type="button" className={bouton} onClick={() => setTraits(undoStroke(traits))} disabled={traits.length === 0}>Undo</button>
        <button type="button" className={bouton} onClick={() => setTraits([])} disabled={traits.length === 0}>Clear</button>
        <span className="ml-auto flex gap-2">
          <button type="button" className={bouton} onClick={onClose}>Cancel</button>
          <button
            type="button"
            onClick={() => onSave(traits)}
            className="rounded-md bg-accent px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-accent-hover"
          >
            Done
          </button>
        </span>
      </div>

      {/* L'image, au plus grand qui tienne, proportions gardées. `min-h-0` : sans lui, cet
          enfant de flex refuse de rétrécir et la barre d'outils sort de l'écran. */}
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 pb-4">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          // `touch-action: none` — sinon un glissement du doigt fait DÉFILER la page au lieu
          // de dessiner, et la fonction est inutilisable sur un téléphone.
          className="max-h-full max-w-full touch-none rounded-lg bg-surface shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
          style={{ cursor: "crosshair" }}
        />
        {/* L'image source, hors écran : elle sert de matière au canevas et n'est jamais
            affichée telle quelle. `onLoad` déclenche la première peinture — sans lui, le
            canevas resterait vide jusqu'au premier trait. */}
        <img ref={imgRef} src={url} alt="" hidden onLoad={() => repeindre(traits, null)} />
      </div>
    </div>
  );
}
